import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { LangProvider, useLp } from "./lib/i18n";
import type { Lang } from "./lib/translations";
import { Landing } from "./routes/Landing";
import { Download } from "./routes/Download";
import { BillingDone } from "./routes/BillingDone";
import { BillingCancelled } from "./routes/BillingCancelled";
import { Account } from "./routes/Account";
import { CompareOtter } from "./routes/CompareOtter";
import { CompareFireflies } from "./routes/CompareFireflies";
import { CompareGranola } from "./routes/CompareGranola";
import { CompareZoomAiCompanion } from "./routes/CompareZoomAiCompanion";
import { CompareKrisp } from "./routes/CompareKrisp";
import { CompareTldv } from "./routes/CompareTldv";
import { CompareTactiq } from "./routes/CompareTactiq";
import { Pricing } from "./routes/Pricing";
import { AiMeetingCopilot } from "./routes/AiMeetingCopilot";
import { SalesEngineerDiscovery } from "./routes/SalesEngineerDiscovery";
import { RecruitingScreeningCall } from "./routes/RecruitingScreeningCall";
import { CustomerSuccessRenewalCall } from "./routes/CustomerSuccessRenewalCall";
import { FreelancerDiscoveryCall } from "./routes/FreelancerDiscoveryCall";
import { TechLeadOneOnOne } from "./routes/TechLeadOneOnOne";
import { ZoomRealtimeTranslation } from "./routes/ZoomRealtimeTranslation";
import { ZoomAutoMeetingNotes } from "./routes/ZoomAutoMeetingNotes";
import { IndependentTechConsultantCall } from "./routes/IndependentTechConsultantCall";
import { LegalDepositionMeetingCopilot } from "./routes/LegalDepositionMeetingCopilot";
import { NoPluginMeetingTranscription } from "./routes/NoPluginMeetingTranscription";
import { PrivateMeetingTranscription } from "./routes/PrivateMeetingTranscription";
import { IaReuniaoIngles } from "./routes/IaReuniaoIngles";
import { SemBotNaReuniao } from "./routes/SemBotNaReuniao";
import { Blog } from "./routes/Blog";
import { BlogPost } from "./routes/BlogPost";
import { Privacy } from "./routes/Privacy";
import { Terms } from "./routes/Terms";
import { Trust } from "./routes/Trust";
import { OAuthCallback } from "./routes/OAuthCallback";
import { Roadmap } from "./routes/Roadmap";
import { Launch } from "./routes/Launch";
import { TeamsWaitlist } from "./routes/TeamsWaitlist";
import { AuthModal } from "./components/AuthModal";
import { PHBanner } from "./components/PHBanner";
import { Founding100Banner } from "./components/Founding100Banner";
import { CookieConsentBanner } from "./components/CookieConsentBanner";

/** 301-equivalent SPA redirect: old on-device slug → new meeting-copilot slug. */
function LegalDepositionSlugRedirect() {
  const lp = useLp();
  return <Navigate to={lp + "/use-cases/legal-deposition-meeting-copilot"} replace />;
}

/** All page routes — reused for each language tree */
function PageRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="launch" element={<Launch />} />
      <Route path="teams-waitlist" element={<TeamsWaitlist />} />
      <Route path="roadmap" element={<Roadmap />} />
      <Route path="downloads" element={<Download />} />
      <Route path="billing/done" element={<BillingDone />} />
      <Route path="billing/cancelled" element={<BillingCancelled />} />
      <Route path="account" element={<Account />} />
      <Route path="compare/otter-vs-minuta" element={<CompareOtter />} />
      <Route path="compare/fireflies-vs-minuta" element={<CompareFireflies />} />
      <Route path="compare/granola-vs-minuta" element={<CompareGranola />} />
      <Route path="compare/zoom-ai-companion-vs-minuta" element={<CompareZoomAiCompanion />} />
      <Route path="compare/krisp-vs-minuta" element={<CompareKrisp />} />
      <Route path="compare/tldv-vs-minuta" element={<CompareTldv />} />
      <Route path="compare/tactiq-vs-minuta" element={<CompareTactiq />} />
      <Route path="pricing" element={<Pricing />} />
      <Route path="ia-reuniao-ingles" element={<IaReuniaoIngles />} />
      <Route path="sem-bot-na-reuniao" element={<SemBotNaReuniao />} />
      <Route path="use-cases/ai-meeting-copilot" element={<AiMeetingCopilot />} />
      <Route path="use-cases/sales-engineer-discovery-call" element={<SalesEngineerDiscovery />} />
      <Route path="use-cases/recruiting-screening-call" element={<RecruitingScreeningCall />} />
      <Route path="use-cases/legal-deposition-on-device" element={<LegalDepositionSlugRedirect />} />
      <Route path="use-cases/legal-deposition-meeting-copilot" element={<LegalDepositionMeetingCopilot />} />
      <Route path="use-cases/customer-success-renewal-call" element={<CustomerSuccessRenewalCall />} />
      <Route path="use-cases/freelancer-discovery-call" element={<FreelancerDiscoveryCall />} />
      <Route path="use-cases/tech-lead-one-on-one" element={<TechLeadOneOnOne />} />
      <Route path="use-cases/zoom-realtime-translation" element={<ZoomRealtimeTranslation />} />
      <Route path="use-cases/zoom-auto-meeting-notes" element={<ZoomAutoMeetingNotes />} />
      <Route path="use-cases/independent-tech-consultant-call" element={<IndependentTechConsultantCall />} />
      <Route path="use-cases/private-meeting-transcription" element={<PrivateMeetingTranscription />} />
      <Route path="use-cases/private-on-device-meeting-transcription" element={<Navigate to="use-cases/private-meeting-transcription" replace />} />
      <Route path="use-cases/no-plugin-meeting-transcription" element={<NoPluginMeetingTranscription />} />
      <Route path="blog" element={<Blog />} />
      <Route path="blog/:slug" element={<BlogPost />} />
      <Route path="privacy" element={<Privacy />} />
      <Route path="terms" element={<Terms />} />
      <Route path="trust" element={<Trust />} />
      <Route path="onde-seus-dados-vao" element={<Trust />} />
      <Route path="donde-van-tus-datos" element={<Trust />} />
      <Route path="auth/callback" element={<OAuthCallback />} />
      <Route path="*" element={<Landing />} />
    </Routes>
  );
}

/** Language wrapper: provides context + nested routes */
function LangApp({ lang }: { lang: Lang }) {
  return (
    <LangProvider lang={lang}>
      {/* Top-of-page strip during the Product Hunt launch window. Self-gates
          on the date range + ?ref=ph query, so it disappears outside the
          window and on routes the user didn't enter via PH. */}
      <PHBanner />
      <Founding100Banner />
      <PageRoutes />
      {/* Auth modal listens to ?auth=login|signup regardless of route */}
      <AuthModal />
      <CookieConsentBanner />
    </LangProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/pt/*" element={<LangApp lang="pt" />} />
        <Route path="/es/*" element={<LangApp lang="es" />} />
        <Route path="*" element={<LangApp lang="en" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
